/**
 * Automatic localStorage cleanup for corrupted arrays
 *
 * Detects and removes corrupted array data that was created by the
 * previous migration bug (spreading arrays into objects with numeric keys)
 *
 * Runs automatically on app startup to ensure clean state
 *
 * @created 2026-02-09
 */

/**
 * Detect if data is a corrupted array
 * (object with numeric keys like "0", "1", "2" but not properly wrapped)
 */
const isCorruptedArray = (data) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return false;
  }

  // Check if it has numeric keys
  const keys = Object.keys(data);
  const numericKeys = keys.filter(k => !isNaN(parseInt(k)));

  // Exclude metadata keys
  const metaKeys = ['vesselId', 'orgId', 'updatedAt', 'syncedAt', '_isArray', 'items', 'migratedFrom'];
  const nonMetaKeys = keys.filter(k => !metaKeys.includes(k));

  // If it has _isArray and items, it's properly wrapped (not corrupted)
  if (data._isArray && data.items) {
    return false;
  }

  // If most non-meta keys are numeric, this is a corrupted array
  return numericKeys.length > 0 && numericKeys.length === nonMetaKeys.length;
};

/**
 * Scan localStorage for corrupted arrays and remove them
 * @returns {Object} { removed: number, keys: Array<string> }
 */
export const cleanupCorruptedLocalStorage = () => {
  console.log('[CLEANUP] Scanning localStorage for corrupted arrays...');

  const removed = [];
  const allKeys = Object.keys(localStorage);

  for (const key of allKeys) {
    try {
      // Skip non-data keys
      if (key.startsWith('sr.') && !key.includes('_')) {
        continue; // System keys
      }

      const rawData = localStorage.getItem(key);
      if (!rawData) continue;

      let data;
      try {
        data = JSON.parse(rawData);
      } catch (e) {
        continue; // Skip unparseable data
      }

      // Check if corrupted
      if (isCorruptedArray(data)) {
        console.warn(`[CLEANUP] Found corrupted array in key: ${key}`);
        localStorage.removeItem(key);
        removed.push(key);
      }
    } catch (error) {
      console.error(`[CLEANUP] Error checking key ${key}:`, error);
    }
  }

  if (removed.length > 0) {
    console.log(`[CLEANUP] ✅ Removed ${removed.length} corrupted localStorage keys:`, removed);
  } else {
    console.log('[CLEANUP] ✅ No corrupted data found - localStorage is clean');
  }

  return {
    removed: removed.length,
    keys: removed
  };
};

/**
 * Run cleanup on app startup (auto-invoked)
 */
if (typeof window !== 'undefined') {
  // Run immediately on import
  try {
    const result = cleanupCorruptedLocalStorage();

    // Expose to window for manual use
    window.__cleanupCorruptedLocalStorage = cleanupCorruptedLocalStorage;

    // If we removed anything, notify user via console
    if (result.removed > 0) {
      console.log(
        '%c[CLEANUP] Corrupted data detected and removed',
        'background: #fbbf24; color: #000; padding: 4px 8px; font-weight: bold;',
        '\nThe app automatically cleaned up corrupted localStorage data from a previous migration.',
        '\nYour data is safe - this was leftover corrupted data that has been removed.'
      );
    }
  } catch (error) {
    console.error('[CLEANUP] Failed to run startup cleanup:', error);
  }
}

export default cleanupCorruptedLocalStorage;
